/**
 * Hero Image Service - Handles hero image management operations
 * Allows admins to select photos from the gallery to use as hero images
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notFoundError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import type { HeroImage, HeroImageFormValues, HeroImageWithPhoto } from '@/types/hero';

export const heroImageService = {
  /**
   * Get all active hero images (for public display)
   * @returns Array of active hero images with photo data
   */
  async getActiveHeroImages(): Promise<ServiceResponse<HeroImageWithPhoto[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const { data, error } = await safeFrom('hero_images')
        .select(`
          *,
          photo:photos(id, image_url, title, album_id)
        `)
        .eq('is_active', true)
        .order('order_index', { ascending: true })
        .order('id', { ascending: true });

      if (error) {
        throw error;
      }

      const heroImages = (data || []).map((item: any) => ({
        ...item,
        photo: item.photo ? {
          ...item.photo,
          image_url: item.photo.image_url || item.photo.url || '',
        } : null,
      })).filter((item: HeroImageWithPhoto) => item.photo !== null) as HeroImageWithPhoto[];

      return successResponse(heroImages);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.getActiveHeroImages'));
    }
  },

  /**
   * Get all hero images (for admin)
   * @returns Array of all hero images with photo data
   */
  async getAllHeroImages(): Promise<ServiceResponse<HeroImageWithPhoto[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const { data, error } = await safeFrom('hero_images')
        .select(`
          *,
          photo:photos(id, image_url, title, album_id)
        `)
        .order('order_index', { ascending: true })
        .order('id', { ascending: true });

      if (error) {
        throw error;
      }

      const heroImages = (data || []).map((item: any) => ({
        ...item,
        photo: item.photo ? {
          ...item.photo,
          image_url: item.photo.image_url || item.photo.url || '',
        } : null,
      }));

      return successResponse(heroImages as HeroImageWithPhoto[]);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.getAllHeroImages'));
    }
  },

  /**
   * Get a single hero image by ID
   * @param id - Hero image ID
   * @returns Hero image with photo data
   */
  async getHeroImage(id: number): Promise<ServiceResponse<HeroImageWithPhoto | null>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { data, error } = await safeFrom('hero_images')
        .select(`
          *,
          photo:photos(id, image_url, title, album_id)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return successResponse(null);
      }

      return successResponse({
        ...data,
        photo: data.photo ? {
          ...data.photo,
          image_url: data.photo.image_url || data.photo.url || '',
        } : null,
      } as HeroImageWithPhoto);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.getHeroImage', { id }));
    }
  },

  /**
   * Create a new hero image
   * @param data - Hero image data
   * @returns Created hero image
   */
  async createHeroImage(data: HeroImageFormValues): Promise<ServiceResponse<HeroImageWithPhoto>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      const { data: heroImage, error } = await safeFrom('hero_images')
        .insert([{
          photo_id: data.photo_id,
          title: data.title?.trim() || null,
          subtitle: data.subtitle?.trim() || null,
          order_index: data.order_index,
          is_active: data.is_active,
          created_by: user?.user?.id || null,
          updated_by: user?.user?.id || null,
        }])
        .select(`
          *,
          photo:photos(id, image_url, title, album_id)
        `)
        .single();

      if (error) {
        throw error;
      }

      const created = {
        ...heroImage,
        photo: heroImage.photo ? {
          ...heroImage.photo,
          image_url: heroImage.photo.image_url || heroImage.photo.url || '',
        } : null,
      } as HeroImageWithPhoto;

      await auditContent.create('hero_image', created.id.toString(), {
        photo_id: created.photo_id,
        title: created.title,
      });

      return successResponse(created);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.createHeroImage', { data }));
    }
  },

  /**
   * Update a hero image
   * @param id - Hero image ID
   * @param data - Updated hero image data
   * @returns Updated hero image
   */
  async updateHeroImage(id: number, data: Partial<HeroImageFormValues>): Promise<ServiceResponse<HeroImageWithPhoto>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      const updateData: any = {
        updated_by: user?.user?.id || null,
      };

      if (data.photo_id !== undefined) updateData.photo_id = data.photo_id;
      if (data.title !== undefined) updateData.title = data.title?.trim() || null;
      if (data.subtitle !== undefined) updateData.subtitle = data.subtitle?.trim() || null;
      if (data.order_index !== undefined) updateData.order_index = data.order_index;
      if (data.is_active !== undefined) updateData.is_active = data.is_active;

      const { data: heroImage, error } = await safeFrom('hero_images')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          photo:photos(id, image_url, title, album_id)
        `)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return errorResponse(notFoundError('Hero image'));
        }
        throw error;
      }

      const updated = {
        ...heroImage,
        photo: heroImage.photo ? {
          ...heroImage.photo,
          image_url: heroImage.photo.image_url || heroImage.photo.url || '',
        } : null,
      } as HeroImageWithPhoto;

      await auditContent.update('hero_image', updated.id.toString(), {
        photo_id: updated.photo_id,
        title: updated.title,
      });

      return successResponse(updated);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.updateHeroImage', { id, data }));
    }
  },

  /**
   * Delete a hero image
   * @param id - Hero image ID
   * @returns Success status
   */
  async deleteHeroImage(id: number): Promise<ServiceResponse<void>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { error } = await safeFrom('hero_images')
        .delete()
        .eq('id', id);

      if (error) {
        if (error.code === 'PGRST116') {
          return errorResponse(notFoundError('Hero image'));
        }
        throw error;
      }

      await auditContent.delete('hero_image', id.toString());
      return successResponse(undefined);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.deleteHeroImage', { id }));
    }
  },

  /**
   * Reorder hero images
   * @param ids - Array of hero image IDs in the desired order
   * @returns Success status
   */
  async reorderHeroImages(ids: number[]): Promise<ServiceResponse<void>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      // Update order_index for each hero image
      const updates = ids.map((id, index) => 
        safeFrom('hero_images')
          .update({ 
            order_index: index,
            updated_by: user?.user?.id || null,
          })
          .eq('id', id)
      );

      await Promise.all(updates);

      return successResponse(undefined);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'heroImageService.reorderHeroImages', { ids }));
    }
  },
};

